import { useState } from 'react';
import { Dropdown, DropdownItem, DropdownMenu, DropdownTrigger, Modal, ModalContent } from '@nextui-org/react';
import { ChevronRight, Dumbbell, Ellipsis, Trash2, Trophy } from 'lucide-react';

import { computeSessionVolume, getWeekBounds } from '../../utils/workoutStats';
import { daysBetween, logDate, recordKindOf, sessionWorkout } from '../../utils/progress';
import { useProgressFormat } from './useProgressFormat';

const PREVIEW_EXERCISES = 3;

// Exercise art is drawn on white to multiply onto a light tile (light in dark mode too); a dumbbell stands in when
// there is none or it fails to load. Round in the feed and in a session, as Hevy draws them
export const ExerciseArt = ({ src, size, isRound = false }) => {
  const [hasImage, setHasImage] = useState(!!src);
  return (
    <span className={`mt-pg-art${isRound ? ' mt-pg-art--round' : ''}`} style={{ width: size, height: size }}>
      {hasImage ? <img src={src} alt="" loading="lazy" onError={() => setHasImage(false)} /> : <Dumbbell aria-hidden="true" />}
    </span>
  );
};

// A link that opens in the page, unless asked for a new tab
const isPlainClick = (event) => event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

// Duração, Volume and, when the session set any, Recordes
export const SessionStats = ({ log, recordCount }) => {
  const format = useProgressFormat();
  const { ui } = format;
  const duration = log.attributes?.duration;
  const volume = computeSessionVolume(log.attributes?.exercises || []);
  return (
    <dl className="mt-pg-stats">
      {duration > 0 && (
        <div>
          <dt>{ui('duration')}</dt>
          <dd>{format.minutes(duration)}</dd>
        </div>
      )}
      {volume > 0 && (
        <div>
          <dt>{ui('volumeTab')}</dt>
          <dd>{format.integer(volume)} kg</dd>
        </div>
      )}
      {recordCount > 0 && (
        <div>
          <dt>{ui('recordsTitle')}</dt>
          <dd>
            <Trophy aria-hidden="true" />
            {recordCount}
          </dd>
        </div>
      )}
    </dl>
  );
};

/**
 * This week's seven days: the workout's letter on each day trained (a tap opens that session), today ringed, the
 * days still to come outlined.
 */
const WeekDays = ({ week, workouts, onOpenSession }) => {
  const format = useProgressFormat();
  const { ui } = format;
  const now = new Date();
  const { start } = getWeekBounds(now);
  return (
    <ol className="mt-pg-days" aria-label={ui('daysLabel')}>
      {Array.from({ length: 7 }, (_, index) => {
        const day = new Date(start.getFullYear(), start.getMonth(), start.getDate() + index);
        // Oldest first, so two sessions in a day read in the order they were done
        const dayLogs = week.logs.filter((log) => daysBetween(logDate(log), day) === 0).reverse();
        const ago = daysBetween(day, now);
        const className = [dayLogs.length && 'is-done', ago === 0 && 'is-today', ago > 0 && 'is-future'].filter(Boolean).join(' ');
        const names = dayLogs.map((log) => format.workoutTitle(sessionWorkout(log, workouts).name));
        return (
          <li key={index} className={className || undefined}>
            <span aria-hidden="true">{format.weekday(day)}</span>
            {dayLogs.length ? (
              <button
                type="button"
                className="mt-pg-days__disc"
                aria-label={ui('dayDone', { day: format.weekdayLong(day), names: names.join(` ${ui('and')} `) })}
                title={names.join(` ${ui('and')} `)}
                onClick={() => onOpenSession(dayLogs[0].id)}
              >
                {dayLogs.map((log) => sessionWorkout(log, workouts).name || '·').join('')}
              </button>
            ) : (
              <>
                <span className="mt-pg-days__disc" aria-hidden="true" />
                <span className="mt-sr-only">{ui('dayEmpty', { day: format.weekdayLong(day) })}</span>
              </>
            )}
          </li>
        );
      })}
    </ol>
  );
};

/**
 * One session in the feed, as Hevy shows it: name, when it started, Duração / Volume (/ Recordes), then the first
 * three exercises. The title is the link; the whole card answers to it
 */
const SessionCard = ({ log, groups, recordCount, workouts, sessionHref, onOpenSession }) => {
  const format = useProgressFormat();
  const { ui } = format;
  const { name, description } = sessionWorkout(log, workouts);
  const hidden = groups.length - PREVIEW_EXERCISES;
  return (
    <li>
      <article className="mt-pg-card">
        <h3 className="mt-pg-card__title">
          <a
            className="mt-pg-card__link"
            href={sessionHref(log.id)}
            data-session-link={log.id}
            onClick={(event) => {
              if (!isPlainClick(event)) return;
              event.preventDefault();
              onOpenSession(log.id);
            }}
          >
            <b>{format.workoutTitle(name)}</b>
            {description && <span> {description}</span>}
          </a>
        </h3>
        <p className="mt-pg-when">{format.startText(log)}</p>
        <SessionStats log={log} recordCount={recordCount} />
        {groups.length > 0 && (
          <ul className="mt-pg-card__ex">
            {groups.slice(0, PREVIEW_EXERCISES).map((group, index) => (
              <li key={`${index}-${group.name}`}>
                <ExerciseArt src={group.thumbnailUrl} size={40} isRound />
                <span>
                  <small>{ui('sets', { count: group.sets.length })}</small> {group.name}
                </span>
              </li>
            ))}
          </ul>
        )}
        {hidden > 0 && <p className="mt-pg-card__more">{ui('seeMore', { count: hidden })}</p>}
      </article>
    </li>
  );
};

/**
 * Treinos: the feed of sessions, newest first, under quiet week labels (this week with its seven days), and
 * "Carregar mais" at the end. Whole weeks only, so a week is never cut in two.
 */
export const SessionFeed = ({ weeks, hasMore, groupsByLog, recordsByLog, workouts, sessionHref, onOpenSession, onLoadMore }) => {
  const format = useProgressFormat();
  const { ui } = format;
  const weekName = (week) => {
    if (week.ago === 0) return ui('thisWeek');
    if (week.ago === 1 && week.weeks === 1) return ui('lastWeek');
    return format.weekRange(week.start, week.end);
  };
  return (
    <div className="mt-pg-feed">
      {weeks.map((week) => {
        const id = `mt-pg-week-${week.start.getTime()}`;
        return (
          <section key={id} className="mt-pg-week" aria-labelledby={id}>
            <header className="mt-pg-week__head">
              <h2 id={id}>{weekName(week)}</h2>
              <span>{week.count ? `${ui('sessionCount', { count: week.count })} · ${format.trainingTime(week.duration)}` : ui('noSessions')}</span>
            </header>
            {week.ago === 0 && <WeekDays week={week} workouts={workouts} onOpenSession={onOpenSession} />}
            {week.count > 0 && (
              <ul className="mt-pg-cards">
                {week.logs.map((log) => (
                  <SessionCard
                    key={log.id}
                    log={log}
                    groups={groupsByLog[log.id] || []}
                    recordCount={Object.keys(recordsByLog[log.id] || {}).length}
                    workouts={workouts}
                    sessionHref={sessionHref}
                    onOpenSession={onOpenSession}
                  />
                ))}
              </ul>
            )}
          </section>
        );
      })}
      {hasMore && (
        <button type="button" className="mt-btn mt-btn--quiet mt-pg-more" onClick={onLoadMore}>
          {ui('loadMore')}
        </button>
      )}
    </div>
  );
};

// Série | Carga e reps: warm-ups marked "A" in a muted warm tone, every other row tinted, a trophy on a record set
const SetTable = ({ group, record }) => {
  const format = useProgressFormat();
  const { ui } = format;
  let working = 0;
  return (
    <table className="mt-pg-sets">
      <thead>
        <tr>
          <th scope="col">{ui('colSet')}</th>
          <th scope="col">{ui('colLoad')}</th>
          <th scope="col">
            <span className="mt-sr-only">{ui('recordShort')}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {group.sets.map((set, index) => {
          if (!set.isWarmup) working += 1;
          const kind = recordKindOf(record, set, group);
          const mark = kind && ui(kind === 'weight' ? 'recordWeight' : 'record1RM');
          return (
            <tr key={index} className={set.isWarmup ? 'is-warmup' : undefined}>
              <th scope="row">
                {set.isWarmup ? (
                  <>
                    <span aria-hidden="true">{ui('warmupShort')}</span>
                    <span className="mt-sr-only">{ui('warmup')}</span>
                  </>
                ) : (
                  <>
                    <span className="mt-sr-only">{ui('colSet')} </span>
                    {working}
                  </>
                )}
              </th>
              <td>{format.setLine(set)}</td>
              <td>
                {mark && (
                  <span className="mt-pg-sets__record" title={mark}>
                    <Trophy aria-hidden="true" />
                    <span className="mt-sr-only">{mark}</span>
                  </span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

/**
 * A session on its own page: the header again with its ⋯ (Apagar registro, behind a confirmation), then every
 * exercise with its sets. An exercise's name opens its evolution.
 */
export const SessionPage = ({ log, groups, records, workouts, isDeleting, onDelete, onOpenExercise }) => {
  const format = useProgressFormat();
  const { ui } = format;
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const { name, description } = sessionWorkout(log, workouts);
  const title = format.workoutTitle(name);

  return (
    <section className="mt-pg-session" aria-labelledby="mt-pg-session-title">
      <header className="mt-pg-session__head">
        <div>
          <h1 id="mt-pg-session-title" className="mt-pg-session__title">
            {title}
            {description && <span> {description}</span>}
          </h1>
          <p className="mt-pg-when">{format.startText(log)}</p>
        </div>
        <Dropdown placement="bottom-end" classNames={{ content: 'mt-root mt-menu' }}>
          <DropdownTrigger>
            <button type="button" className="mt-pg-icon-btn" aria-label={ui('more', { name: title })}>
              <Ellipsis aria-hidden="true" />
            </button>
          </DropdownTrigger>
          <DropdownMenu aria-label={ui('more', { name: title })} itemClasses={{ base: 'mt-menu__item' }} onAction={() => setConfirmOpen(true)}>
            <DropdownItem key="delete" className="mt-menu__item--danger" startContent={<Trash2 aria-hidden="true" />}>
              {ui('deleteLog')}
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </header>

      <SessionStats log={log} recordCount={Object.keys(records || {}).length} />

      <div className="mt-pg-session__ex">
        {groups.map((group, index) => {
          const id = `mt-pg-sx-${index}`;
          return (
            <section key={id} className="mt-pg-sx" aria-labelledby={id}>
              <h2 className="mt-pg-sx__title">
                <button type="button" id={id} className="mt-pg-sx__head" title={ui('seeEvolution', { name: group.name })} onClick={() => onOpenExercise(group.name)}>
                  <ExerciseArt src={group.thumbnailUrl} size={44} isRound />
                  <span>{group.name}</span>
                  <span className="mt-sr-only">, {ui('seeEvolutionShort')}</span>
                  <ChevronRight aria-hidden="true" />
                </button>
              </h2>
              <SetTable group={group} record={records?.[group.name]} />
            </section>
          );
        })}
      </div>

      <Modal
        isOpen={isConfirmOpen}
        onOpenChange={setConfirmOpen}
        placement="center"
        hideCloseButton
        classNames={{ base: 'mt-root mt-dialog mt-pg-dialog', backdrop: 'mt-dialog__backdrop' }}
      >
        <ModalContent>
          {(onClose) => (
            <div className="mt-dialog__body">
              <h2 className="mt-dialog__title">{ui('deleteTitle')}</h2>
              <p className="mt-dialog__text">{ui('deleteBody')}</p>
              <div className="mt-dialog__actions">
                <button type="button" className="mt-btn mt-btn--quiet" onClick={onClose} disabled={isDeleting}>
                  {ui('cancel')}
                </button>
                <button
                  type="button"
                  className="mt-btn mt-btn--danger"
                  disabled={isDeleting}
                  onClick={async () => {
                    if (await onDelete(log.id)) onClose();
                  }}
                >
                  {ui('deleteConfirm')}
                </button>
              </div>
            </div>
          )}
        </ModalContent>
      </Modal>
    </section>
  );
};
